'use client';

import * as React from 'react';
import { useActionState } from 'react';
import { useFormStatus } from 'react-dom';

import { loginAction, type LoginState } from './actions';

const INITIAL_STATE: LoginState = {};

export function LoginForm() {
  const [state, formAction] = useActionState(loginAction, INITIAL_STATE);

  return (
    <form
      action={formAction}
      noValidate
      style={{ display: 'grid', gap: 'var(--s-5)' }}
    >
      <Field
        id='email'
        name='email'
        type='email'
        label='Work email'
        autoComplete='email'
        placeholder='you@staffy.com'
        required
      />
      <PasswordField />

      <div
        role='alert'
        aria-live='polite'
        style={{
          minHeight: 18,
          fontSize: 12.5,
          lineHeight: 1.4,
        }}
      >
        {state.error ? (
          <span
            key={state.error}
            className='auth-error'
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: 'var(--orange-dark)',
            }}
          >
            <ErrorGlyph />
            <span>{state.error}</span>
          </span>
        ) : null}
      </div>

      <SubmitButton />
    </form>
  );
}

interface FieldProps {
  id: string;
  name: string;
  type: 'email' | 'password';
  label: string;
  autoComplete: string;
  placeholder?: string;
  required?: boolean;
}

function Field({
  id,
  name,
  type,
  label,
  autoComplete,
  placeholder,
  required,
}: FieldProps) {
  return (
    <label
      htmlFor={id}
      className='auth-field'
      style={{ display: 'grid', gap: 6 }}
    >
      <span
        className='auth-field-label'
        style={{
          fontSize: 12.5,
          fontWeight: 600,
          letterSpacing: 'var(--tracking-body)',
          color: 'var(--text-secondary)',
        }}
      >
        {label}
      </span>
      <span
        className='auth-input-shell'
        style={{
          display: 'grid',
          alignItems: 'center',
          height: 44,
          background: 'var(--offwhite)',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--r-md)',
        }}
      >
        <input
          id={id}
          name={name}
          type={type}
          autoComplete={autoComplete}
          placeholder={placeholder}
          required={required}
          style={{
            height: '100%',
            width: '100%',
            padding: '0 14px',
            background: 'transparent',
            border: 0,
            outline: 'none',
            fontSize: 14,
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-sans)',
            letterSpacing: 'var(--tracking-body)',
          }}
        />
      </span>
    </label>
  );
}

function PasswordField() {
  const [show, setShow] = React.useState(false);
  return (
    <div className='auth-field' style={{ display: 'grid', gap: 6 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--s-4)',
        }}
      >
        <label
          htmlFor='password'
          className='auth-field-label'
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            letterSpacing: 'var(--tracking-body)',
            color: 'var(--text-secondary)',
          }}
        >
          Password
        </label>
        <a
          href='mailto:ops@staffy.com?subject=Password%20reset'
          className='auth-link'
          style={{
            fontSize: 11.5,
            color: 'var(--text-secondary)',
            textDecoration: 'none',
          }}
        >
          Forgot it?
        </a>
      </div>
      <span
        className='auth-input-shell'
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          alignItems: 'center',
          height: 44,
          background: 'var(--offwhite)',
          border: '1px solid var(--border-strong)',
          borderRadius: 'var(--r-md)',
        }}
      >
        <input
          id='password'
          name='password'
          type={show ? 'text' : 'password'}
          autoComplete='current-password'
          required
          style={{
            height: '100%',
            width: '100%',
            padding: '0 14px',
            background: 'transparent',
            border: 0,
            outline: 'none',
            fontSize: 14,
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-sans)',
            letterSpacing: 'var(--tracking-body)',
          }}
        />
        <button
          type='button'
          onClick={() => setShow((v) => !v)}
          aria-pressed={show}
          aria-label={show ? 'Hide password' : 'Show password'}
          className='auth-show-toggle'
          style={{
            marginRight: 6,
            height: 32,
            padding: '0 10px',
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            color: 'var(--text-tertiary)',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 'var(--tracking-body)',
            borderRadius: 'var(--r-sm)',
            transition: 'color 140ms var(--easing), background-color 140ms var(--easing)',
          }}
        >
          {show ? 'Hide' : 'Show'}
        </button>
      </span>
      <style>
        {`
          .auth-show-toggle:hover {
            color: var(--text-primary);
            background: color-mix(in oklch, var(--navy) 6%, transparent);
          }
        `}
      </style>
    </div>
  );
}

function SubmitButton() {
  const { pending } = useFormStatus();
  return (
    <button
      type='submit'
      disabled={pending}
      aria-disabled={pending}
      className='auth-submit'
      style={{
        marginTop: 4,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        height: 46,
        padding: '0 20px',
        background: pending
          ? 'color-mix(in oklch, var(--orange) 80%, var(--navy))'
          : 'var(--orange)',
        color: 'var(--offwhite)',
        border: 0,
        borderRadius: 'var(--r-md)',
        fontFamily: 'var(--font-sans)',
        fontSize: 14,
        fontWeight: 650,
        letterSpacing: 'var(--tracking-body)',
        cursor: pending ? 'progress' : 'pointer',
        boxShadow: 'var(--shadow-accent)',
        transition:
          'background-color 180ms var(--easing), transform 180ms var(--easing), box-shadow 180ms var(--easing)',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          opacity: pending ? 0.85 : 1,
        }}
      >
        {pending ? 'Signing in' : 'Sign in'}
      </span>
      <span aria-hidden className='auth-submit-chev'>
        {pending ? (
          <Spinner />
        ) : (
          <svg viewBox='0 0 24 24' width={13} height={13}>
            <path
              d='M5 12h13m-5-5 5 5-5 5'
              fill='none'
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2.2'
            />
          </svg>
        )}
      </span>

      <style>
        {`
          .auth-submit:hover:not(:disabled) {
            background: var(--orange-dark) !important;
            transform: translateY(-1px);
            box-shadow:
              0 1px 0 color-mix(in oklch, var(--orange-dark) 36%, transparent),
              0 14px 28px -8px color-mix(in oklch, var(--orange-dark) 50%, transparent);
          }
          .auth-submit:active:not(:disabled) {
            transform: translateY(0) scale(0.99);
            box-shadow: var(--shadow-accent);
            transition-duration: 80ms;
          }
          .auth-submit:focus-visible {
            outline: none;
            box-shadow:
              var(--shadow-accent),
              0 0 0 3px color-mix(in oklch, var(--orange) 30%, transparent);
          }
          @keyframes auth-spin { to { transform: rotate(360deg); } }
          @media (prefers-reduced-motion: reduce) {
            .auth-submit:hover:not(:disabled),
            .auth-submit:active:not(:disabled) {
              transform: none !important;
            }
          }
        `}
      </style>
    </button>
  );
}

function Spinner() {
  return (
    <svg
      viewBox='0 0 24 24'
      width={13}
      height={13}
      style={{ animation: 'auth-spin 0.9s linear infinite' }}
    >
      <circle
        cx='12'
        cy='12'
        r='9'
        fill='none'
        stroke='currentColor'
        strokeOpacity='0.3'
        strokeWidth='2.4'
      />
      <path
        d='M21 12a9 9 0 0 0-9-9'
        fill='none'
        stroke='currentColor'
        strokeWidth='2.4'
        strokeLinecap='round'
      />
    </svg>
  );
}

function ErrorGlyph() {
  return (
    <svg viewBox='0 0 24 24' width={13} height={13} aria-hidden>
      <circle
        cx='12'
        cy='12'
        r='9'
        fill='none'
        stroke='currentColor'
        strokeWidth='1.8'
      />
      <path
        d='M12 8v5M12 16.2v.1'
        stroke='currentColor'
        strokeWidth='1.8'
        strokeLinecap='round'
      />
    </svg>
  );
}
